import { ref } from 'vue';
import { useFileDrop } from '@/js/core/composables/useFileDrop';
import {
  ALLOWED_IMAGE_TYPES,
  MAX_UPLOAD_SIZE,
  MAX_UPLOAD_SIZE_LABEL,
} from '@/js/core/constants/media-upload';
import { uploadMedia } from '@/js/core/utils/trays-api';

const fileNameFromUrl = url => {
  if (!url) return '';
  const last = url.split('/').pop()?.split('?')[0] ?? '';
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

export function useFeaturedMediaUpload() {
  const imageUrl = ref(null);
  const attachmentId = ref(null);
  const focalPoint = ref(null);
  const fileName = ref('');
  const alt = ref('');
  const caption = ref('');
  const isUploading = ref(false);
  const uploadError = ref('');
  const originalAttachmentId = ref(null);
  const originalFocalPoint = ref(null);
  const originalAlt = ref('');
  const originalCaption = ref('');
  const mediaRemoved = ref(false);

  const cleanup = () => {
    if (imageUrl.value?.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl.value);
    }
  };

  const setMedia = ({
    imageUrl: nextImageUrl = null,
    attachmentId: nextAttachmentId = null,
    focalPoint: nextFocalPoint = null,
    alt: nextAlt = '',
    caption: nextCaption = '',
  } = {}) => {
    cleanup();
    imageUrl.value = nextImageUrl;
    attachmentId.value = nextAttachmentId;
    focalPoint.value = nextFocalPoint ? { ...nextFocalPoint } : null;
    fileName.value = fileNameFromUrl(nextImageUrl);
    alt.value = nextAlt ?? '';
    caption.value = nextCaption ?? '';
    originalAttachmentId.value = nextAttachmentId;
    originalFocalPoint.value = nextFocalPoint ? { ...nextFocalPoint } : null;
    originalAlt.value = nextAlt ?? '';
    originalCaption.value = nextCaption ?? '';
    mediaRemoved.value = false;
    uploadError.value = '';
  };

  const processFile = async file => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      uploadError.value = 'Invalid file type. Please upload JPEG, PNG, GIF, or WebP.';
      return;
    }

    if (file.size > MAX_UPLOAD_SIZE) {
      uploadError.value = `File is too large. Maximum size is ${MAX_UPLOAD_SIZE_LABEL}.`;
      return;
    }

    cleanup();
    imageUrl.value = URL.createObjectURL(file);
    fileName.value = file.name;
    focalPoint.value = null;
    alt.value = '';
    caption.value = '';
    uploadError.value = '';
    isUploading.value = true;
    mediaRemoved.value = true;

    try {
      const response = await uploadMedia(file);
      const result = response.data;
      attachmentId.value = result.attachment_id;
      cleanup();
      imageUrl.value = result.url;
    } catch (err) {
      uploadError.value = err.message || 'Upload failed. Please try again.';
      cleanup();
      imageUrl.value = null;
      attachmentId.value = null;
    } finally {
      isUploading.value = false;
    }
  };

  const removeImage = () => {
    cleanup();
    imageUrl.value = null;
    attachmentId.value = null;
    focalPoint.value = null;
    fileName.value = '';
    alt.value = '';
    caption.value = '';
    uploadError.value = '';
    mediaRemoved.value = true;
  };

  const createPayload = () => {
    const payload = {
      featuredMediaId: attachmentId.value,
      focalPoint: attachmentId.value ? focalPoint.value : null,
    };

    if (attachmentId.value) {
      payload.featuredMediaAlt = alt.value;
      payload.featuredMediaCaption = caption.value;
    }

    return payload;
  };

  const updatePayload = () => {
    if (mediaRemoved.value && !attachmentId.value) {
      return {
        featuredMediaId: null,
        focalPoint: null,
      };
    }

    if (attachmentId.value !== originalAttachmentId.value) {
      return {
        featuredMediaId: attachmentId.value,
        focalPoint: attachmentId.value ? focalPoint.value : null,
        featuredMediaAlt: attachmentId.value ? alt.value : null,
        featuredMediaCaption: attachmentId.value ? caption.value : null,
      };
    }

    const payload = {};

    if (JSON.stringify(focalPoint.value) !== JSON.stringify(originalFocalPoint.value)) {
      Object.assign(payload, {
        focalPoint: focalPoint.value,
      });
    }

    if (alt.value !== originalAlt.value) {
      payload.featuredMediaAlt = alt.value;
    }

    if (caption.value !== originalCaption.value) {
      payload.featuredMediaCaption = caption.value;
    }

    return payload;
  };

  const { handleFileChange, handleFileDrop, handleDragOver } = useFileDrop(processFile);

  return {
    imageUrl,
    attachmentId,
    focalPoint,
    fileName,
    alt,
    caption,
    isUploading,
    uploadError,
    handleFileChange,
    handleFileDrop,
    handleDragOver,
    removeImage,
    cleanup,
    setMedia,
    createPayload,
    updatePayload,
  };
}
